import { C } from "./colors";

export const WASTE_STREAMS = [
  "MSW","Cardboard","Mixed Recycling","Glass","Metal","Plastic","Paper","Food Waste","Compost",
  "Green Waste","Biosolids","Hazardous Waste","Universal Waste","Medical Waste","Construction Debris",
  "Mattresses","Tires","E-Waste","Confidential Shredding","Grease/FOG","Other",
];
// Pre-expansion stream names — records/accounts saved before this list grew still carry
// these values. They're intentionally excluded from WASTE_STREAMS (so new input is
// steered to the current names) but must keep validating, rendering, and charting
// correctly wherever they already exist.
export const LEGACY_WASTE_STREAMS = ["Recycling","Hazardous"];

export const WASTE_CSV_HEADERS = ["date","location_name","stream","volume_cubic_yards","weight_tons","weight_lbs","cost_dollars"];
export const MONTH_ABBR = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// Fixed categorical color per stream (never cycled) — built entirely from the app's
// existing palette so multi-series charts stay on-brand. Streams left unassigned here
// (the rarer ones) fall back to C.muted wherever this map is read — never a crash.
export const STREAM_COLORS = {
  MSW:C.navy, Cardboard:C.orange, "Mixed Recycling":C.teal, Glass:C.purple, Metal:C.yellow,
  Plastic:C.tealLight, Paper:C.orangeLight, "Food Waste":C.green, Compost:C.greenLight,
  "Grease/FOG":C.purpleLight, "Hazardous Waste":C.red, "Medical Waste":C.redLight,
  "Green Waste":C.navyLight, Other:C.muted,
  // Legacy aliases — old data keeps the color of its closest current equivalent.
  Recycling:C.teal, Hazardous:C.red,
};

// For a <select> editing a record that may still carry a legacy stream name: the
// canonical list, plus that record's current value appended if it's not already in it —
// so the dropdown never silently shows blank/unmatched for old data.
export function streamOptionsWithLegacy(currentValue){
  if(currentValue&&!WASTE_STREAMS.includes(currentValue)) return [...WASTE_STREAMS,currentValue];
  return WASTE_STREAMS;
}

export function parseCSVLine(line){
  const out=[]; let cur=""; let inQuotes=false;
  for(let i=0;i<line.length;i++){
    const ch=line[i];
    if(inQuotes){
      if(ch==='"'){ if(line[i+1]==='"'){cur+='"';i++;} else inQuotes=false; }
      else cur+=ch;
    }else{
      if(ch==='"') inQuotes=true;
      else if(ch===','){out.push(cur);cur="";}
      else cur+=ch;
    }
  }
  out.push(cur);
  return out.map(s=>s.trim());
}

// Parses & validates the Waste Data Hub CSV format. All-or-nothing: any row error
// aborts the whole import so partial/garbled data never lands in Firestore.
export function parseWasteCSV(text){
  const lines=text.split(/\r\n|\n|\r/).filter(l=>l.trim().length>0);
  if(lines.length===0) return {records:[],errors:["The file is empty."]};
  const header=parseCSVLine(lines[0]).map(h=>h.toLowerCase());
  const headerOk=header.length===WASTE_CSV_HEADERS.length&&WASTE_CSV_HEADERS.every((h,i)=>h===header[i]);
  if(!headerOk) return {records:[],errors:[`Header row must be exactly: ${WASTE_CSV_HEADERS.join(",")}`]};
  if(lines.length===1) return {records:[],errors:["No data rows found below the header."]};

  const parseNum=(s,field,rowNum)=>{
    if(s==="") return {value:null};
    const n=Number(s);
    if(Number.isNaN(n)) return {error:`Row ${rowNum}: "${field}" value "${s}" is not a valid number.`};
    return {value:n};
  };

  const records=[]; const errors=[];
  for(let i=1;i<lines.length;i++){
    const rowNum=i+1;
    const cols=parseCSVLine(lines[i]);
    if(cols.length!==WASTE_CSV_HEADERS.length){
      errors.push(`Row ${rowNum}: expected ${WASTE_CSV_HEADERS.length} columns, found ${cols.length}.`);
      continue;
    }
    const [date,locationName,stream,volumeStr,weightTonsStr,weightLbsStr,costStr]=cols;
    let rowErr=null;
    if(!/^\d{4}-(0[1-9]|1[0-2])$/.test(date)) rowErr=`Row ${rowNum}: "date" value "${date}" must be in YYYY-MM format.`;
    else if(!locationName) rowErr=`Row ${rowNum}: "location_name" is required.`;
    else if(!WASTE_STREAMS.includes(stream)) rowErr=`Row ${rowNum}: "stream" value "${stream}" must be one of ${WASTE_STREAMS.join(", ")}.`;

    let volume=null,weightTons=null,weightLbs=null,costDollars=null;
    if(!rowErr){const r=parseNum(volumeStr,"volume_cubic_yards",rowNum); if(r.error)rowErr=r.error; else volume=r.value;}
    if(!rowErr){const r=parseNum(weightTonsStr,"weight_tons",rowNum); if(r.error)rowErr=r.error; else weightTons=r.value;}
    if(!rowErr){const r=parseNum(weightLbsStr,"weight_lbs",rowNum); if(r.error)rowErr=r.error; else weightLbs=r.value;}
    if(!rowErr){const r=parseNum(costStr,"cost_dollars",rowNum); if(r.error)rowErr=r.error; else costDollars=r.value;}

    if(rowErr){errors.push(rowErr);continue;}
    records.push({date,locationName,stream,volume,weightTons,weightLbs,costDollars});
  }
  return {records,errors};
}

// Which numeric field represents "the" volume/weight metric for this org, per its configured unit
export function wasteMetricOf(record,config){
  if(config.hasVolume) return record.volume;
  if(config.hasWeight) return config.weightUnit==="lbs"?record.weightLbs:record.weightTons;
  return null;
}
export function wasteMetricUnitLabel(config){
  if(config.hasVolume) return "cu yd";
  if(config.hasWeight) return config.weightUnit==="lbs"?"lbs":"tons";
  return "";
}
